import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MARCHE_FULL } from '../../data/masterData';

export interface CompatModel { id: string; nome: string; marca: string }

interface Props {
  /** Array strutturato dal abbinatore (prioritario su `compatibility`) */
  modelli?: CompatModel[];
  /** Stringa CSV fallback: "Canon MF 244dw, Canon MF 4450, ..." */
  compatibility?: string;
  productTitle?: string;
}

interface CompatItem { label: string; id?: string }
interface CompatGroup { marca: string; items: CompatItem[] }

// Marche note (lookup dbo_Marche), ordinate per lunghezza decrescente così
// da riconoscere prima i nomi composti (es. "KYOCERA MITA" prima di "KYOCERA").
const KNOWN_BRANDS = Array.from(new Set(MARCHE_FULL.map(m => m.nomeMarca.toUpperCase())))
  .sort((a, b) => b.length - a.length);

/** "KYOCERA MITA" → "Kyocera Mita", "HP" → "HP" (acronimi ≤2 lettere restano maiuscoli) */
function titleCaseBrand(brand: string): string {
  return brand.split(' ').map(w => (w.length <= 2 ? w : w.charAt(0) + w.slice(1).toLowerCase())).join(' ');
}

/** Raggruppa i modelli compatibili per marca (a partire da `modelli` strutturati o dal CSV `compatibility` di fallback). */
export function buildCompatGroups(modelli?: CompatModel[], compatibility?: string): CompatGroup[] {
  const map = new Map<string, CompatGroup>();
  const push = (marcaRaw: string, label: string, id?: string) => {
    const key = marcaRaw.trim().toUpperCase() || 'ALTRO';
    if (!map.has(key)) map.set(key, { marca: key === 'ALTRO' ? 'Altro' : titleCaseBrand(key), items: [] });
    map.get(key)!.items.push({ label, id });
  };

  if (modelli && modelli.length > 0) {
    modelli.forEach(m => push(m.marca, m.nome.trim(), m.id));
  } else if (compatibility) {
    compatibility.split(',').map(s => s.trim()).filter(Boolean).forEach(raw => {
      const upper = raw.toUpperCase();
      const matched = KNOWN_BRANDS.find(b => upper === b || upper.startsWith(b + ' '));
      if (matched) {
        push(matched, raw.slice(matched.length).trim() || raw);
      } else {
        push('Altro', raw);
      }
    });
  }
  // Ordina i GRUPPI alfabeticamente per marca, e ogni gruppo alfabeticamente
  // per nome modello — così l'elenco completo (raggruppato per marca)
  // mostra sempre lo stesso ordine deterministico ad ogni render,
  // indipendentemente dall'ordine con cui l'API restituisce le
  // associazioni. Fondamentale anche perché l'ANTEPRIMA piatta (vedi
  // `getCompatPreview`) riusa esattamente questo stesso ordine: i primi
  // elementi dell'anteprima devono coincidere con i primi elementi che si
  // vedono espandendo le compatibilità (richiesta utente 22/02/2026).
  const result = Array.from(map.values());
  result.forEach(g => g.items.sort((a, b) => a.label.localeCompare(b.label, 'it', { sensitivity: 'base' })));
  result.sort((a, b) => a.marca.localeCompare(b.marca, 'it', { sensitivity: 'base' }));
  return result;
}

export interface CompatPreviewItem { label: string; id?: string }

/** Anteprima piatta (max `limit` elementi, "Marca Modello" o solo "Modello"
 * se `includeBrand=false`) + conteggio totale — usata sotto "Cod. DTK" nella
 * lista articoli. La lista ESPANSA (`CompatibilityChips`, raggruppata per
 * marca) non è mai affetta da questo parametro. */
/** Anteprima piatta (max `limit` elementi, "Marca Modello" o solo "Modello"
 * se `includeBrand=false`) + conteggio totale — usata sotto "Cod. DTK" nella
 * lista articoli. La lista ESPANSA (`CompatibilityChips`, raggruppata per
 * marca) non è mai affetta dai parametri di questa funzione.
 *
 * `searchedModel`: se fornito (pagina "Consumabili per {modello}", ricerca
 * per modello) il modello ricercato viene mostrato SEMPRE come primo
 * elemento dell'anteprima, seguito dagli altri nell'ordine normale — SOLO
 * l'anteprima è affetta, la vista espansa resta invariata. Nota: l'API
 * `/api/abbinatore/modello/{id}/skus` esclude deliberatamente il modello
 * ricercato dalla lista compatibilità di ogni articolo (è il soggetto
 * della ricerca, non un'altra compatibilità), quindi va aggiunto come voce
 * sintetica in testa — non si trova già tra gli item esistenti (richiesta
 * utente 22/02/2026). */
export function getCompatPreview(modelli?: CompatModel[], compatibility?: string, limit = 3, includeBrand = true, searchedModel?: { id: string; nome: string; marca: string }): { items: CompatPreviewItem[]; total: number } {
  const groups = buildCompatGroups(modelli, compatibility);
  const flat: CompatPreviewItem[] = [];
  groups.forEach(g => g.items.forEach(it => flat.push({ label: (includeBrand && g.marca !== 'Altro') ? `${g.marca} ${it.label}` : it.label, id: it.id })));
  // Nessun sort qui: l'ordine deriva direttamente dai gruppi (già
  // alfabetici per marca, poi per modello, vedi buildCompatGroups) — è
  // lo STESSO ordine che si vede espandendo le compatibilità, garantendo
  // che l'anteprima mostri sempre i primi elementi coerenti con l'elenco
  // completo.
  const total = flat.length;
  if (searchedModel) {
    const filtered = flat.filter(it => it.id !== searchedModel.id);
    const label = includeBrand ? `${searchedModel.marca} ${searchedModel.nome}` : searchedModel.nome;
    filtered.unshift({ label, id: searchedModel.id });
    return { items: filtered.slice(0, limit), total };
  }
  return { items: flat.slice(0, limit), total: flat.length };
}

/**
 * Elenco COMPLETO dei modelli compatibili, RAGGRUPPATO PER MARCA, mostrato
 * SOTTO le colonne della riga prodotto. La visibilità (espanso/collassato)
 * è pilotata dal genitore (bottone di espansione sotto "Cod. DTK") — questo
 * componente renderizza sempre tutti i gruppi/modelli, senza troncamento
 * né bottone proprio.
 * Ogni chip con un modello riconosciuto (id disponibile) è cliccabile e
 * avvia la ricerca per compatibilità di quel modello.
 */
export function CompatibilityChips({ modelli, compatibility, productTitle }: Props) {
  const groups = useMemo(() => buildCompatGroups(modelli, compatibility), [modelli, compatibility]);

  if (groups.length === 0) return null;

  const chipClass = "text-xs font-medium text-[hsl(214,58%,41%)] underline decoration-1 decoration-[hsl(214,58%,41%)]/40 underline-offset-2 max-w-[180px] truncate transition-colors";

  return (
    <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-1.5" data-testid="compatibility-chips" title={productTitle}>
      <span className="text-xs font-semibold text-muted-foreground flex-shrink-0 whitespace-nowrap">Modelli Compatibili:</span>

      {groups.map((g, gi) => (
        <div
          key={g.marca}
          className={`flex flex-wrap items-baseline gap-x-1.5 gap-y-1 ${gi === 0 ? '' : 'basis-full w-full'}`}
        >
          <span className="text-xs font-bold uppercase tracking-wide text-[#3a3a36] flex-shrink-0" data-testid={`compat-group-${g.marca}`}>{g.marca}</span>
          {g.items.map((it, i) => (
            it.id ? (
              <Link
                key={i}
                to={`/consumabili-per/${it.id}`}
                onClick={e => e.stopPropagation()}
                className={`${chipClass} hover:text-[hsl(214,58%,30%)] hover:decoration-[hsl(214,58%,30%)] cursor-pointer`}
                title={`Cerca consumabili per ${g.marca} ${it.label}`}
                data-testid="compat-chip-link"
              >
                {it.label}
              </Link>
            ) : (
              <span key={i} className={chipClass} title={`${g.marca} ${it.label}`}>
                {it.label}
              </span>
            )
          ))}
        </div>
      ))}
    </div>
  );
}
